import { useCallback, useEffect, useState } from 'react';
import { photos } from '../photos';
import ScatterWords from '../components/ScatterWords';
import { ArrowLeft, ArrowRight, Close } from '../components/Icons';

const pad = (n) => String(n).padStart(2, '0');

// One song title from Where Did Everybody Go? per photo. Seven positions against eight
// songs, so the pairings keep shifting down the page instead of repeating in a visible pattern.
const WORDS = [
  'Where Did Everybody Go?', 'Daylight', 'Pink Walls', 'Not Okay..',
  'hello there, you', 'Cold', 'Ashtray', 'Monica',
];
// Each spot also caps its width inside the photo, so a long title wraps instead of running off the edge.
const SPOTS = [
  'left-[6%] top-[7%] max-w-[88%] text-3xl md:text-5xl',
  'bottom-[9%] right-[7%] max-w-[86%] text-right text-xl md:text-3xl',
  'bottom-[10%] left-[7%] max-w-[86%] text-4xl md:text-6xl',
  'right-[6%] top-[8%] max-w-[88%] text-right text-lg md:text-2xl',
  'left-[36%] top-[40%] max-w-[58%] text-2xl md:text-4xl',
  'left-[8%] top-[48%] max-w-[84%] text-base md:text-xl',
  'bottom-[14%] right-[30%] max-w-[64%] text-right text-3xl md:text-4xl',
].map((spot) => `${spot} !whitespace-normal`);

// On desktop the grid becomes a carousel, one 3×2 page at a time.
const PER_PAGE = 6;
const DESKTOP = '(min-width: 768px)';

const useMedia = (query) => {
  const get = () => typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

const navBtn =
  'flex h-11 w-11 items-center justify-center border border-bone/25 font-times text-lg transition-colors hover:border-mustard hover:bg-mustard hover:text-ink';

export default function Gallery() {
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(0);
  const desktop = useMedia(DESKTOP);
  const pages = Math.ceil(photos.length / PER_PAGE);

  const turn = useCallback((dir) => setPage((p) => (p + dir + pages) % pages), [pages]);

  // Stepping through the viewer carries the carousel along, so closing it lands on the same page.
  useEffect(() => {
    if (active !== null) setPage(Math.floor(active / PER_PAGE));
  }, [active]);

  useEffect(() => {
    if (!desktop || active !== null) return;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') turn(1);
      if (e.key === 'ArrowLeft') turn(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [desktop, active, turn]);

  const tile = (src, i) => (
    <button key={src} onClick={() => setActive(i)} aria-label={`Open photo ${i + 1}`} className="group block">
      <div className="relative overflow-hidden">
        <img
          src={src}
          alt={`Frame ${pad(i + 1)}`}
          loading="lazy"
          className="aspect-[1280/865] w-full object-cover grayscale transition duration-700 ease-out md:group-hover:scale-[1.03] md:group-hover:grayscale-0"
        />
        <ScatterWords words={[[WORDS[i % WORDS.length], SPOTS[i % SPOTS.length]]]} />
      </div>
    </button>
  );

  const step = useCallback(
    (dir) => setActive((i) => (i + dir + photos.length) % photos.length),
    []
  );

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setActive(null);
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active, step]);

  return (
    <main>
      <section className="bg-black px-2 py-2 text-bone md:px-3 md:py-3">
        <h1 className="sr-only">My family</h1>
        {desktop ? (
          <>
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] motion-reduce:transition-none"
                style={{ transform: `translateX(-${page * 100}%)` }}
              >
                {Array.from({ length: pages }, (_, p) => (
                  <div
                    key={p}
                    aria-hidden={p !== page}
                    inert={p !== page}
                    className="grid w-full shrink-0 grid-cols-3 grid-rows-2 gap-3"
                  >
                    {photos.slice(p * PER_PAGE, (p + 1) * PER_PAGE).map((src, j) => tile(src, p * PER_PAGE + j))}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between px-2 pb-2 pt-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-mustard/80">
                {pad(page + 1)} / {pad(pages)}
              </span>
              <div className="flex gap-3">
                <button onClick={() => turn(-1)} aria-label="Previous page" className={navBtn}>
                  <ArrowLeft />
                </button>
                <button onClick={() => turn(1)} aria-label="Next page" className={navBtn}>
                  <ArrowRight />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 gap-2">{photos.map(tile)}</div>
        )}
      </section>

      {active !== null && (
        <div role="dialog" aria-label="Photo viewer" className="fixed inset-0 z-[60] flex flex-col bg-ink text-bone">
          <div className="flex items-center justify-between px-5 py-5 font-mono text-[11px] uppercase tracking-[0.25em] md:px-10">
            <span className="text-mustard/80">
              {pad(active + 1)} / {pad(photos.length)}
            </span>
            <button onClick={() => setActive(null)} className="flex items-center gap-2 font-times text-lg normal-case tracking-normal opacity-70 transition-opacity hover:opacity-100">
              Close <Close />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 items-center justify-center px-5 md:px-16" onClick={() => setActive(null)}>
            <img
              src={photos[active]}
              alt={`Frame ${pad(active + 1)}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full max-w-full object-contain "
            />
          </div>

          <div className="flex items-center justify-center gap-3 py-6">
            <button onClick={() => step(-1)} aria-label="Previous" className={navBtn}>
              <ArrowLeft />
            </button>
            <button onClick={() => step(1)} aria-label="Next" className={navBtn}>
              <ArrowRight />
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
